
import {
  defineComponent, computed, reactive, onBeforeMount,
} from 'vue';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import Icone from '@/core/components/Icone.vue';
import ColunaGrade from '@/core/components/Tela/ColunaGrade.vue';
import { ECustomRenderRow, IColumn } from '@/core/models/AntDesign/IColumn';
import { IListaPaginadaMetaData } from '@/core/models/Consulta/IListaPaginada';
import { useGradeBase } from '@/core/composables/GradeBase';
import UtilitarioMascara from '@/core/utilitarios/UtilitarioMascara';
import { useTelaBase } from '@/core/composables/TelaBase';
import UtilitarioData from '@/core/utilitarios/UtilitarioData';
import { IPagination } from '@/core/models/AntDesign/IPagination';
import MensagemSemDados from '@/core/components/Tela/MensagemSemDados.vue';
import UtilitarioDispositivo from '@/core/utilitarios/UtilitarioDispositivo';
import { IDTODevolucaoCompras } from '@/models/DTO/Compras/IDTODevolucaoCompras';

export default defineComponent({
  name: 'DevolucaoComprasListagem',
  components: {
    Icone,
    ColunaGrade,
    MensagemSemDados,
  },
  props: {
    visualizacaoKanban: {
      type: Boolean,
      default: true,
    },
    devolucoes: {
      type: Array as () => IDTODevolucaoCompras[],
      required: true,
    },
    selecionados: {
      type: Array as () => number[],
      required: true,
    },
    paginacao: {
      type: Object as () => IPagination,
      required: true,
    },
  },
  emits: ['update:devolucoes', 'update:visualizacaoKanban', 'update:paginacao', 'update:selecionados', 'selecaoAlterada', 'buscarDevolucoes', 'visualizar', 'imprimir', 'novoLancamento'],
  setup(props, { emit }) {
    const { telaBase } = useTelaBase();
    const { gradeBase } = useGradeBase();

    const state = reactive({
      colunas: [] as IColumn[],
      paginacaoApi: { totalRegistros: 0, paginaAtual: 1 } as IListaPaginadaMetaData,
      timerRegistros: 0,
      totalRegistrosComMascara: '100',
      telaMobile: false,
    });

    const computedDevolucoes = computed({
      get: () => props.devolucoes,
      set: (valor: IDTODevolucaoCompras[]) => {
        emit('update:devolucoes', valor);
      },
    });

    const computedVisualizacaoKanban = computed({
      get: () => props.visualizacaoKanban,
      set: (valor: boolean) => {
        emit('update:visualizacaoKanban', valor);
      },
    });

    const computedSelecionados = computed({
      get: () => props.selecionados,
      set: (valor: number[]) => {
        emit('update:selecionados', valor);
      },
    });

    const computedPaginacao = computed({
      get: () => props.paginacao,
      set: (valor: IPagination) => {
        emit('update:paginacao', valor);
      },
    });

    function obterClasseStatus(status: string) {
      switch (status) {
        case 'Em digitação':
          return 'em-digitacao';
        case 'Aguardando emissão':
          return 'aguardando-emissao';
        case 'Concluído':
          return 'concluido';
        default:
          return 'cancelado';
      }
    }

    function preencherColunas() {
      if (state.telaMobile) {
        state.colunas = [
          {
            title: 'Código', dataIndex: 'codigo', key: 'Codigo', position: 1, visible: false,
          },
          {
            title: 'Devolucao', dataIndex: 'devolucao', key: 'DevolucaoCompras', position: 2, visible: true, ellipsis: true,
          },
        ];
      } else {
        state.colunas = [
          {
            title: 'PK', dataIndex: 'codigo', key: 'Codigo', position: 1, visible: false, ordering: true, fixed: 'left', customRenderRow: ECustomRenderRow.LinkAcao,
          },
          {
            title: 'Devolução', dataIndex: 'numero', key: 'Numero', position: 1, visible: true, ordering: true, fixed: 'left', customRenderRow: ECustomRenderRow.LinkAcao, width: 110,
          },
          {
            title: 'Nº da Nota', dataIndex: 'numeroNotaFiscal', key: 'NumeroNotaFiscal', position: 3, visible: true, ordering: true, width: 100,
          },
          {
            title: 'Status', dataIndex: 'descricaoStatus', key: 'Status', position: 2, visible: true, ordering: true, width: 250, align: 'center',
          },
          {
            title: 'Modelo', dataIndex: 'modelo', key: 'Modelo', position: 4, visible: true, ordering: true, width: 75, align: 'center',
          },
          {
            title: 'Dt de Emissão', dataIndex: 'dataEmissao', key: 'DataEmissao', position: 5, visible: true, ordering: true, customRenderRow: ECustomRenderRow.MascararData, width: 120, align: 'center',
          },
          {
            title: 'Emitente', dataIndex: 'nomeRazaoSocialEmitente', key: 'NomeRazaoSocialEmitente', position: 6, visible: true, ordering: true,
          },
          {
            title: 'Valor Total', dataIndex: 'totalNotaFiscal', key: 'totalNotaFiscal', position: 7, visible: true, ordering: true, customRenderRow: ECustomRenderRow.MascararFracionado, align: 'right', width: 100,
          },
          {
            title: 'Ações', key: 'acoes', position: 8, visible: true, fixed: 'right', width: 100, align: 'center', customRenderRow: ECustomRenderRow.IconeAcoes,
          },
        ];
      }
    }

    async function buscarDevolucoes() {
      emit('buscarDevolucoes', telaBase.ordenacaoSelecionada, { ...gradeBase.filtrosCabecalho });
    }

    async function alterarQuantidadeRegistroTotal(valor: any) {
      clearTimeout(state.timerRegistros);
      let totalRegistros = 100;
      state.timerRegistros = setTimeout(async () => {
        if (UtilitarioGeral.valorValido(valor)) {
          const limiteRegistros = UtilitarioMascara.removerMascaraNumero(valor);
          if (limiteRegistros > 0) {
            totalRegistros = limiteRegistros;
          }
        }
        computedPaginacao.value.total = totalRegistros;
        state.totalRegistrosComMascara = UtilitarioMascara.mascararNumeroInteiro(totalRegistros);
        buscarDevolucoes();
      }, 600);
    }

    async function mudarPagina(current: number, pageSize: number) {
      computedPaginacao.value.current = current;
      computedPaginacao.value.pageSize = pageSize;
      buscarDevolucoes();
    }

    async function mostrarNovaPagina(current: number, pageSize: number) {
      computedPaginacao.value.current = 1;
      computedPaginacao.value.pageSize = pageSize;
      buscarDevolucoes();
    }

    function mostrarTotalPaginacao(total: number, range: [number, number]) {
      return `${UtilitarioMascara.mascararNumeroInteiro(range[0])} - ${UtilitarioMascara.mascararNumeroInteiro(range[1])} de ${UtilitarioMascara.mascararNumeroInteiro(total)} registros`;
    }

    function keyupQuantidadeRegistroTotal(event: KeyboardEvent) {
      const input = event.target as HTMLInputElement | null;
      alterarQuantidadeRegistroTotal(input?.value ?? '');
    }

    const mudarSelecao = (selectedRowKeys: any) => {
      computedSelecionados.value = selectedRowKeys;
      emit('selecaoAlterada');
    };

    function visualizar(index: number) {
      emit('visualizar', props.devolucoes[index].codigo);
    }

    function imprimir(index: number) {
      emit('imprimir', props.devolucoes[index].codigo);
    }

    function novoLancamento() {
      emit('novoLancamento');
    }

    onBeforeMount(async () => {
      state.telaMobile = UtilitarioDispositivo.larguraTelaMobile();
      preencherColunas();
      computedPaginacao.value.total = 100;
      computedPaginacao.value.showSizeChanger = true;
      computedPaginacao.value.pageSize = 100;
      computedPaginacao.value.pageSizeOptions = ['10', '50', '100', '500', '1000'];
    });

    return {
      state,
      props,
      window,
      telaBase,
      gradeBase,
      computedDevolucoes,
      computedVisualizacaoKanban,
      computedSelecionados,
      computedPaginacao,
      UtilitarioMascara,
      UtilitarioData,
      mudarPagina,
      buscarDevolucoes,
      mostrarNovaPagina,
      alterarQuantidadeRegistroTotal,
      mostrarTotalPaginacao,
      keyupQuantidadeRegistroTotal,
      obterClasseStatus,
      mudarSelecao,
      ECustomRenderRow,
      visualizar,
      imprimir,
      novoLancamento,
    };
  },
});

